<script setup>
import { computed, onActivated, onDeactivated, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useIntervalFn } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import API from 'dashboard/api/talkviaEmailCampaigns';
import { emailCampaignCopy } from '../emailCampaignCopy';
import EmailCampaignEditor from './EmailCampaignEditor.vue';
import EmailCampaignReview from './EmailCampaignReview.vue';
import { campaignStatus, campaignError, dateLabel } from '../emailCampaigns';

const copy = emailCampaignCopy;

const store = useStore();
const route = useRoute();
let requestVersion = 0;
const campaigns = ref([]);
const total = ref(0);
const page = ref(1);
const loading = ref(false);
const busy = ref(false);
const editor = ref(null);
const review = ref(null);
const confirmation = ref(null);
const target = ref(null);
const pollFailed = ref(false);
const hasEmail = computed(() =>
  store.getters['inboxes/getInboxes'].some(
    inbox => inbox.channel_type === 'Channel::Email'
  )
);

const load = async (silent = false) => {
  if (loading.value) return;
  requestVersion += 1;
  const version = requestVersion;
  loading.value = true;
  try {
    const { data } = await API.get(page.value);
    if (version !== requestVersion) return;
    campaigns.value = data.campaigns;
    total.value = data.total;
    pollFailed.value = false;
  } catch (error) {
    if (version !== requestVersion) return;
    if (!silent || !pollFailed.value) useAlert(campaignError(error));
    pollFailed.value = true;
  } finally {
    if (version === requestVersion) loading.value = false;
  }
};

const { pause, resume } = useIntervalFn(
  () => {
    if (
      campaigns.value.some(campaign =>
        ['sending', 'scheduled'].includes(campaign.status)
      )
    )
      load(true);
  },
  15000,
  { immediate: false }
);

onActivated(() => {
  load();
  resume();
});
onDeactivated(pause);

watch(
  () => route.params.accountId,
  () => {
    requestVersion += 1;
    loading.value = false;
    campaigns.value = [];
    total.value = 0;
    page.value = 1;
    editor.value?.close();
    review.value?.close();
    confirmation.value?.close();
    load();
  }
);

const saved = async campaign => {
  await load();
  review.value.open(campaign);
};

const changeStatus = async (campaign, action) => {
  if (busy.value) return;
  busy.value = true;
  try {
    await API[action](campaign.id);
    useAlert(
      action === 'pause'
        ? 'Campanha pausada. Um envio já iniciado pode terminar.'
        : 'Campanha retomada. Apenas os destinatários na fila serão enviados.'
    );
    await load();
  } catch (error) {
    useAlert(campaignError(error));
  } finally {
    busy.value = false;
  }
};

const askDelete = campaign => {
  target.value = campaign;
  confirmation.value.open();
};

const remove = async () => {
  if (busy.value || !target.value) return;
  busy.value = true;
  try {
    await API.delete(target.value.id);
    useAlert(
      target.value.status === 'draft'
        ? 'Rascunho excluído.'
        : 'Campanha cancelada. O histórico foi preservado.'
    );
    confirmation.value.close();
    await load();
  } catch (error) {
    useAlert(campaignError(error));
  } finally {
    busy.value = false;
  }
};

const navigate = async offset => {
  page.value += offset;
  await load();
};
</script>

<template>
  <section
    class="flex h-full min-w-0 flex-col overflow-y-auto bg-n-background p-4 sm:p-6"
  >
    <header class="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <h1 class="m-0 text-xl font-semibold text-n-slate-12">
          {{ copy.title }}
        </h1>
        <p class="mt-1 mb-0 text-sm text-n-slate-11">{{ copy.description }}</p>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          type="button"
          :label="copy.refresh"
          variant="faded"
          color="slate"
          :is-loading="loading"
          @click="load()"
        />
        <Button
          type="button"
          :label="copy.newCampaign"
          icon="i-lucide-plus"
          :disabled="!hasEmail"
          @click="editor.open()"
        />
      </div>
    </header>
    <div
      v-if="!hasEmail"
      class="mb-5 rounded-xl border border-n-weak bg-n-solid-2 p-5"
    >
      <p class="mt-0 text-sm text-n-slate-12">{{ copy.connectHint }}</p>
      <RouterLink
        :to="{ name: 'settings_inbox_new' }"
        class="text-sm text-n-brand"
      >
        {{ copy.connect }}
      </RouterLink>
    </div>
    <p
      v-if="!campaigns.length"
      class="py-8 text-center text-sm text-n-slate-11"
    >
      {{ loading ? copy.loading : copy.empty }}
    </p>
    <div class="flex flex-col gap-3">
      <article
        v-for="campaign in campaigns"
        :key="campaign.id"
        class="min-w-0 rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <h2
              class="mt-0 mb-1 break-words text-base font-semibold text-n-slate-12"
            >
              {{ campaign.title }}
            </h2>
            <p class="m-0 break-words text-sm text-n-slate-11">
              {{ campaign.subject }}
            </p>
            <p class="mt-2 mb-0 text-xs text-n-slate-11">
              {{ campaign.inbox_name }}{{ copy.separator }}
              {{ dateLabel(campaign.scheduled_at) }}
            </p>
          </div>
          <span
            role="status"
            class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
            >{{ campaignStatus[campaign.status] }}</span
          >
        </div>
        <p
          v-if="campaign.status !== 'draft'"
          class="mt-3 mb-0 text-sm text-n-slate-11"
        >
          {{
            `${copy.sent}: ${campaign.counts.sent || 0}${copy.separator}${copy.pending}: ${campaign.counts.pending || 0}${copy.separator}${copy.failed}: ${campaign.counts.failed || 0}${copy.separator}${copy.uncertain}: ${campaign.counts.uncertain || 0}`
          }}
        </p>
        <div class="mt-4 flex flex-wrap gap-2">
          <Button
            v-if="campaign.status === 'draft'"
            type="button"
            :label="copy.edit"
            variant="faded"
            color="slate"
            @click="editor.open(campaign)"
          />
          <Button
            type="button"
            :label="
              campaign.status === 'draft'
                ? 'Revisar e confirmar'
                : 'Ver resultado'
            "
            variant="faded"
            color="slate"
            @click="review.open(campaign)"
          />
          <Button
            v-if="['sending', 'scheduled'].includes(campaign.status)"
            type="button"
            :label="copy.pause"
            variant="faded"
            color="slate"
            :disabled="busy"
            @click="changeStatus(campaign, 'pause')"
          />
          <Button
            v-if="campaign.status === 'paused'"
            type="button"
            :label="copy.resume"
            variant="faded"
            color="slate"
            :disabled="busy"
            @click="changeStatus(campaign, 'resume')"
          />
          <Button
            v-if="!['completed', 'cancelled'].includes(campaign.status)"
            type="button"
            :label="
              campaign.status === 'draft' ? 'Excluir' : 'Cancelar campanha'
            "
            variant="faded"
            color="ruby"
            :disabled="busy"
            @click="askDelete(campaign)"
          />
        </div>
      </article>
    </div>
    <div
      v-if="total > 20"
      class="mt-5 flex flex-wrap items-center justify-end gap-2"
    >
      <Button
        type="button"
        :label="copy.previous"
        variant="faded"
        color="slate"
        :disabled="page === 1 || loading"
        @click="navigate(-1)"
      />
      <span class="text-sm text-n-slate-11">{{ `${copy.page} ${page}` }}</span>
      <Button
        type="button"
        :label="copy.next"
        variant="faded"
        color="slate"
        :disabled="page * 20 >= total || loading"
        @click="navigate(1)"
      />
    </div>
    <EmailCampaignEditor ref="editor" @saved="saved" />
    <EmailCampaignReview ref="review" @dispatched="load()" />
    <Dialog
      ref="confirmation"
      type="alert"
      :title="
        target?.status === 'draft' ? 'Excluir rascunho?' : 'Cancelar campanha?'
      "
      :description="
        target?.status === 'draft'
          ? 'O rascunho será removido.'
          : 'Novos envios serão interrompidos. Um envio já iniciado pode terminar. O histórico será mantido.'
      "
      :confirm-button-label="
        target?.status === 'draft' ? 'Excluir' : 'Cancelar campanha'
      "
      :is-loading="busy"
      @confirm="remove"
    />
  </section>
</template>
